import { useCallback, useEffect, useState } from 'react';
import { Loader2, Power, RefreshCw, ShieldCheck, Trash2, UserPlus, UsersRound } from 'lucide-react';
import type { FormEvent } from 'react';
import {
  CLOUD_ROLES,
  CLOUD_ROLE_DESCRIPTIONS,
  CLOUD_ROLE_LABELS,
} from '../../core/cloud-auth';
import type { CloudRole, CloudUser } from '../../core/cloud-auth';
import { formatPhone, isValidMobile, normalizePhone } from '../../core/phone';
import { hasPermission } from '../../core/permissions';
import {
  createCloudUser,
  deleteCloudUser,
  listCloudUsers,
  updateCloudUser,
} from '../../db/cloud-auth-store';
import { fieldClass } from '../../components/ui/form-classes';
import { Modal } from '../../components/ui/Modal';
import type { User } from '../../types/user';

interface ManageUsersPageProps {
  user: User;
}

type Notice = { tone: 'ok' | 'error'; text: string } | null;

/**
 * "ניהול משתמשים" — the central directory, edited where it lives.
 *
 * Every row on this screen is a row in Cloudflare D1, not in this device's
 * IndexedDB: adding somebody here lets their phone into the system from any
 * device in the unit, and removing them locks it out of all of them. That is
 * the whole point of the screen, and it is why it does not fall back to a local
 * list when the cloud is unreachable — a directory that showed a stale copy
 * would be a directory somebody would act on.
 *
 * The screen is reached only by a מנהל על (`MANAGE_USERS`), and the API refuses
 * everybody else regardless (`functions/api/_middleware.js`). The refusal that
 * matters most is the one it puts on the account looking at it: an admin cannot
 * disable, demote or delete themselves, because `MANAGE_USERS` is the only way
 * back and they would be taking it away from the unit.
 */
export function ManageUsersPage({ user }: ManageUsersPageProps) {
  const [users, setUsers] = useState<CloudUser[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<Notice>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<CloudUser | null>(null);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState<CloudRole>('manager');
  const [adding, setAdding] = useState(false);

  const allowed = hasPermission(user, 'MANAGE_USERS');

  const load = useCallback(async () => {
    if (!allowed) return;
    setLoading(true);
    const result = await listCloudUsers();
    setUsers(result.ok ? result.data ?? [] : null);
    if (!result.ok) setNotice({ tone: 'error', text: result.error ?? 'טעינת הספרייה נכשלה' });
    setLoading(false);
  }, [allowed]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!allowed) {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <section className="bg-panel border border-line rounded-2xl p-6 text-center">
          <ShieldCheck size={28} className="mx-auto text-ink/30" aria-hidden />
          <h2 className="mt-3 font-semibold">ניהול משתמשים שמור למנהל המערכת</h2>
          <p className="text-sm text-ink/60 mt-1">
            רמת ההרשאה שלכם מאפשרת ניהול מלא של משימות, חיילים ורכבים — ואינה כוללת את ספריית
            המשתמשים.
          </p>
        </section>
      </div>
    );
  }

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    const normalized = normalizePhone(phone);
    if (!trimmed) {
      setNotice({ tone: 'error', text: 'שם מלא הוא שדה חובה' });
      return;
    }
    if (!isValidMobile(normalized)) {
      setNotice({ tone: 'error', text: 'מספר לא תקין — נדרש מספר נייד ישראלי (05XXXXXXXX)' });
      return;
    }

    setAdding(true);
    setNotice(null);
    const result = await createCloudUser({ name: trimmed, phone: normalized, role });
    setAdding(false);

    if (!result.ok) {
      setNotice({ tone: 'error', text: result.error ?? 'פתיחת החשבון נכשלה' });
      return;
    }
    setName('');
    setPhone('');
    setRole('manager');
    setNotice({
      tone: 'ok',
      text: `${trimmed} נוסף לספרייה. הכניסה: מספר הטלפון וסיסמת הקבוצה.`,
    });
    await load();
  };

  const handleToggle = async (entry: CloudUser) => {
    setBusyId(entry.id);
    setNotice(null);
    const result = await updateCloudUser(entry.id, { isActive: !entry.isActive });
    setBusyId(null);
    if (!result.ok) {
      setNotice({ tone: 'error', text: result.error ?? 'העדכון נכשל' });
      return;
    }
    setNotice({
      tone: 'ok',
      text: `${entry.name} — הגישה ${entry.isActive ? 'הושבתה' : 'הופעלה'}.`,
    });
    await load();
  };

  const handleRole = async (entry: CloudUser, next: CloudRole) => {
    if (next === entry.role) return;
    setBusyId(entry.id);
    setNotice(null);
    const result = await updateCloudUser(entry.id, { role: next });
    setBusyId(null);
    if (!result.ok) {
      setNotice({ tone: 'error', text: result.error ?? 'שינוי רמת ההרשאה נכשל' });
      return;
    }
    setNotice({ tone: 'ok', text: `${entry.name} — רמת ההרשאה שונתה ל״${CLOUD_ROLE_LABELS[next]}״.` });
    await load();
  };

  const handleDelete = async () => {
    if (!confirming) return;
    setBusyId(confirming.id);
    setNotice(null);
    const result = await deleteCloudUser(confirming.id);
    setBusyId(null);
    if (!result.ok) {
      setNotice({ tone: 'error', text: result.error ?? 'המחיקה נכשלה' });
      return;
    }
    setNotice({ tone: 'ok', text: `${confirming.name} הוסר מהספרייה.` });
    setConfirming(null);
    await load();
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4">
      <section className="bg-panel border border-line rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <UsersRound size={20} className="text-brand-500" aria-hidden />
              ניהול משתמשים מרכזי
            </h2>
            <p className="text-sm text-ink/60 mt-1">
              הספרייה שמורה בענן ומשותפת לכל המכשירים. מי שנמצא כאן ומסומן כפעיל נכנס מכל טלפון
              עם מספרו וסיסמת הקבוצה; מי שהוסר או הושבת — לא נכנס בשום מכשיר.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="shrink-0 h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink flex items-center gap-1.5 disabled:opacity-40"
          >
            <RefreshCw size={15} aria-hidden />
            רענון
          </button>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          {CLOUD_ROLES.map((entry) => (
            <div key={entry} className="rounded-xl border border-line p-3">
              <dt className="font-medium">{CLOUD_ROLE_LABELS[entry]}</dt>
              <dd className="text-xs text-ink/60 mt-1">{CLOUD_ROLE_DESCRIPTIONS[entry]}</dd>
            </div>
          ))}
        </dl>
      </section>

      {notice ? (
        <p
          className={[
            'rounded-2xl border p-3 text-sm',
            notice.tone === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-rose-300 bg-rose-50 text-rose-700',
          ].join(' ')}
          role="status"
        >
          {notice.text}
        </p>
      ) : null}

      <section className="bg-panel border border-line rounded-2xl p-5">
        <h3 className="font-semibold flex items-center gap-2">
          <UserPlus size={18} className="text-brand-500" aria-hidden />
          הוספת משתמש לספרייה
        </h3>
        <form onSubmit={handleAdd} className="mt-3 grid sm:grid-cols-4 gap-3" noValidate>
          <label className="text-sm sm:col-span-1">
            <span className="block text-xs text-ink/50 mb-1">שם מלא</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className={fieldClass}
              disabled={adding}
            />
          </label>
          <label className="text-sm sm:col-span-1">
            <span className="block text-xs text-ink/50 mb-1">טלפון</span>
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              placeholder="050-000-0000"
              value={formatPhone(normalizePhone(phone))}
              onChange={(event) => setPhone(normalizePhone(event.target.value).slice(0, 10))}
              className={fieldClass}
              data-dir="ltr"
              disabled={adding}
            />
          </label>
          <label className="text-sm sm:col-span-1">
            <span className="block text-xs text-ink/50 mb-1">רמת הרשאה</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as CloudRole)}
              className={fieldClass}
              disabled={adding}
            >
              {CLOUD_ROLES.map((entry) => (
                <option key={entry} value={entry}>
                  {CLOUD_ROLE_LABELS[entry]}
                </option>
              ))}
            </select>
          </label>
          <div className="sm:col-span-1 flex items-end">
            <button
              type="submit"
              disabled={adding}
              className="w-full h-10 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-700 disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {adding ? <Loader2 size={16} className="animate-spin" aria-hidden /> : null}
              פתיחת חשבון
            </button>
          </div>
        </form>
      </section>

      <section className="bg-panel border border-line rounded-2xl p-5">
        <h3 className="font-semibold mb-3">
          חשבונות בספרייה {users ? `(${users.length})` : ''}
        </h3>

        {loading ? (
          <p className="text-sm text-ink/60 flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" aria-hidden />
            טוען את הספרייה מהענן…
          </p>
        ) : users === null ? (
          <p className="text-sm text-ink/60">
            אין כרגע חיבור לספרייה בענן. שאר המערכת ממשיכה לעבוד מקומית — נסו רענון כשיש קליטה.
          </p>
        ) : users.length === 0 ? (
          <p className="text-sm text-ink/60">הספרייה ריקה.</p>
        ) : (
          <ul className="space-y-2">
            {users.map((entry) => {
              const isSelf = entry.id === user.cloudId;
              const working = busyId === entry.id;
              return (
                <li
                  key={entry.id}
                  className="rounded-xl border border-line p-3 flex flex-wrap items-center gap-3"
                >
                  <div className="min-w-0 grow">
                    <p className="font-medium truncate">
                      {entry.name}
                      {isSelf ? <span className="text-xs text-ink/50 me-2"> · אתם</span> : null}
                    </p>
                    <p className="text-xs text-ink/60" data-dir="ltr">
                      {formatPhone(entry.phone)}
                    </p>
                  </div>

                  <span
                    className={[
                      'text-xs rounded-lg px-2 py-1 border',
                      entry.isActive
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                        : 'border-line bg-canvas text-ink/50',
                    ].join(' ')}
                  >
                    {entry.isActive ? 'פעיל' : 'מושבת'}
                  </span>

                  <select
                    value={entry.role}
                    onChange={(event) => void handleRole(entry, event.target.value as CloudRole)}
                    disabled={working || isSelf}
                    className={`${fieldClass} w-auto`}
                    aria-label={`רמת הרשאה — ${entry.name}`}
                  >
                    {CLOUD_ROLES.map((option) => (
                      <option key={option} value={option}>
                        {CLOUD_ROLE_LABELS[option]}
                      </option>
                    ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => void handleToggle(entry)}
                    disabled={working || isSelf}
                    title={entry.isActive ? 'השבתת גישה' : 'הפעלת גישה'}
                    className="h-9 px-3 rounded-xl border border-line text-sm text-ink/70 hover:text-ink disabled:opacity-40 flex items-center gap-1.5"
                  >
                    <Power size={15} aria-hidden />
                    {entry.isActive ? 'השבתה' : 'הפעלה'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setConfirming(entry)}
                    disabled={working || isSelf}
                    title="הסרה מהספרייה"
                    className="h-9 px-3 rounded-xl border border-rose-300 bg-rose-50 text-rose-700 text-sm hover:bg-rose-100 disabled:opacity-40 flex items-center gap-1.5"
                  >
                    <Trash2 size={15} aria-hidden />
                    הסרה
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {confirming ? (
        <Modal
          title="הסרת משתמש מהספרייה"
          subtitle={confirming.name}
          onClose={() => setConfirming(null)}
          footer={
            <>
              <button
                type="button"
                onClick={() => setConfirming(null)}
                className="h-10 px-4 rounded-xl border border-line text-sm"
              >
                ביטול
              </button>
              <button
                type="button"
                disabled={busyId === confirming.id}
                onClick={() => void handleDelete()}
                className="h-10 px-4 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 disabled:opacity-40"
              >
                הסרה מהספרייה
              </button>
            </>
          }
        >
          <p className="text-sm">
            המספר {formatPhone(confirming.phone)} יפסיק להיכנס למערכת מכל מכשיר. המשימות, השיבוצים
            ורשומות יומן הביקורת שנרשמו על שמם נשארים כפי שהם.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
